import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { ROUTES } from '@/shared/config/routes';
import { formatDate } from '@/shared/lib/utils/date';
import { HIGH_FETCH_PRIORITY } from '@/shared/lib/utils/imgPriority';
import type { ArticleSummary } from '@/entities/article';

/** Large hero card for the most recent article on the home page. */
export function FeaturedArticle({ article }: { article: ArticleSummary }) {
  return (
    <Link
      to={ROUTES.article(article.slug)}
      className="group relative block min-h-[320px] overflow-hidden rounded-3xl bg-zinc-900"
    >
      {article.coverUrl ? (
        // Decorative: the headline below carries the meaning. But it is the home
        // page's LCP element, so it loads eagerly at high priority — Core Web
        // Vitals are a ranking signal.
        <img
          src={article.coverUrl}
          alt=""
          loading="eager"
          decoding="async"
          {...HIGH_FETCH_PRIORITY}
          className="absolute inset-0 h-full w-full object-cover opacity-60 transition-all duration-700 group-hover:scale-[1.03] group-hover:opacity-70"
        />
      ) : null}
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
      <div className="absolute left-5 top-5">
        <span className="rounded-full border border-amber-400/30 bg-black/40 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-400 backdrop-blur-sm">
          {article.category}
        </span>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-7 sm:p-9">
        <h2 className="mb-4 font-display text-3xl font-black italic leading-tight text-white sm:text-4xl">
          {article.title}
        </h2>
        <div className="flex flex-wrap items-center gap-4 font-mono text-xs text-zinc-400">
          {article.publishedAt ? <span>{formatDate(article.publishedAt)}</span> : null}
          <span className="text-zinc-600">·</span>
          <span>{article.readTime} min read</span>
          <span className="ml-auto flex items-center gap-1.5 text-xs font-bold text-amber-400 transition-all group-hover:gap-2.5">
            Read <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
